import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "wouter";
import { useAuth } from "@clerk/react";
import { useOrg } from "@/lib/orgContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { format } from "date-fns";
import {
  ShieldAlert,
  Download,
  FileText,
  ArrowRight,
  X,
  Filter,
  Search,
  Loader2,
  ExternalLink,
  Globe,
  Monitor,
} from "lucide-react";
import { PersonaBadge } from "@/components/PersonaBadge";
import type { Persona } from "@/lib/orgContext";

interface AuditEntry {
  id: number;
  entityType: "contract" | "coach" | "contract_document";
  entityId: number;
  entityLabel: string;
  entityHref: string | null;
  action: "create" | "update" | "delete" | "status_change" | "upload" | "remove" | "download";
  field: string | null;
  fieldLabel: string | null;
  oldValue: string | null;
  newValue: string | null;
  actorClerkUserId: string | null;
  actorName: string | null;
  actorPersonas: string[];
  actorIsGuest?: boolean;
  createdAt: string;
}

interface DownloadMeta {
  type?: string;
  fileName?: string;
  documentCount?: number;
  ip?: string;
  userAgent?: string;
  label?: string;
}

const ACTION_OPTIONS: { value: string; label: string }[] = [
  { value: "__all", label: "All actions" },
  { value: "download", label: "Download" },
  { value: "upload", label: "Upload" },
  { value: "remove", label: "Remove document" },
  { value: "create", label: "Create" },
  { value: "update", label: "Update" },
  { value: "status_change", label: "Status change" },
  { value: "delete", label: "Delete" },
];

const ENTITY_OPTIONS: { value: string; label: string }[] = [
  { value: "__all", label: "All entities" },
  { value: "contract", label: "Contract" },
  { value: "coach", label: "Coach" },
  { value: "contract_document", label: "Contract document" },
];

const PERSONA_OPTIONS: { value: string; label: string }[] = [
  { value: "__all", label: "All personas" },
  { value: "Finance", label: "Finance" },
  { value: "HR", label: "HR" },
  { value: "Legal", label: "Legal" },
];

const ACTION_BADGES: Record<AuditEntry["action"], { label: string; cls: string }> = {
  create: { label: "Created", cls: "bg-green-500/10 text-green-700 border-green-200" },
  update: { label: "Updated", cls: "bg-blue-500/10 text-blue-700 border-blue-200" },
  delete: { label: "Deleted", cls: "bg-destructive/10 text-destructive border-destructive/30" },
  status_change: { label: "Status", cls: "bg-amber-500/10 text-amber-700 border-amber-200" },
  upload: { label: "Uploaded", cls: "bg-purple-500/10 text-purple-700 border-purple-200" },
  remove: { label: "Removed", cls: "bg-orange-500/10 text-orange-700 border-orange-200" },
  download: { label: "Downloaded", cls: "bg-sky-500/10 text-sky-700 border-sky-200" },
};

const ENTITY_LABELS: Record<AuditEntry["entityType"], string> = {
  contract: "Contract",
  coach: "Coach",
  contract_document: "Document",
};

function tryParseJson<T>(s: string | null): T | null {
  if (!s) return null;
  try { return JSON.parse(s) as T; } catch { return null; }
}

function actionBadge(action: AuditEntry["action"]) {
  const m = ACTION_BADGES[action] ?? { label: action, cls: "bg-muted text-muted-foreground border-border" };
  return <Badge variant="outline" className={m.cls} data-testid={`badge-action-${action}`}>{m.label}</Badge>;
}

function summarize(e: AuditEntry): React.ReactNode {
  if (e.field) {
    const oldV = e.oldValue ?? "—";
    const newV = e.newValue ?? "—";
    return (
      <div className="text-sm flex items-center gap-2 flex-wrap">
        <span className="font-medium text-muted-foreground">{e.fieldLabel ?? e.field}:</span>
        <span className="line-through text-muted-foreground max-w-[200px] truncate" title={oldV}>{oldV}</span>
        <ArrowRight className="h-3 w-3 text-muted-foreground" />
        <span className="font-medium max-w-[240px] truncate" title={newV}>{newV}</span>
      </div>
    );
  }
  if (e.action === "download") {
    const d = tryParseJson<DownloadMeta>(e.newValue);
    if (d?.type === "zip") return <span className="text-sm">Downloaded all documents ({d.documentCount ?? "?"} files)</span>;
    if (d?.fileName) return <span className="text-sm">Downloaded: <span className="font-medium">{d.fileName}</span></span>;
    return <span className="text-sm text-muted-foreground italic">Document downloaded</span>;
  }
  if (e.action === "upload") {
    const d = tryParseJson<DownloadMeta>(e.newValue);
    return <span className="text-sm">Uploaded: <span className="font-medium">{d?.fileName ?? "document"}</span></span>;
  }
  if (e.action === "remove") {
    const d = tryParseJson<DownloadMeta>(e.oldValue);
    return <span className="text-sm">Removed: <span className="font-medium">{d?.fileName ?? "document"}</span></span>;
  }
  if (e.action === "delete") return <span className="text-sm text-muted-foreground italic">Record deleted</span>;
  if (e.action === "create") return <span className="text-sm text-muted-foreground italic">Record created</span>;
  return <span className="text-sm text-muted-foreground italic">Change recorded</span>;
}

function downloadDetails(e: AuditEntry): { ip?: string; userAgent?: string; fileName?: string } | null {
  if (e.action !== "download") return null;
  const d = tryParseJson<DownloadMeta>(e.newValue);
  if (!d) return null;
  return { ip: d.ip, userAgent: d.userAgent, fileName: d.fileName };
}

export default function AuditLogPage() {
  const { getToken } = useAuth();
  const { org, loading: orgLoading } = useOrg();
  const [, setLocation] = useLocation();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [actionFilter, setActionFilter] = useState<string>("__all");
  const [entityFilter, setEntityFilter] = useState<string>("__all");
  const [personaFilter, setPersonaFilter] = useState<string>("__all");
  const [actorFilter, setActorFilter] = useState<string>("");
  const [searchFilter, setSearchFilter] = useState<string>("");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");

  const isAdmin = org?.role === "admin";

  useEffect(() => {
    if (!orgLoading && org && !isAdmin) {
      setLocation("/settings");
    }
  }, [orgLoading, org, isAdmin, setLocation]);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const token = await getToken();
        const params = new URLSearchParams();
        if (actionFilter && actionFilter !== "__all") params.set("action", actionFilter);
        if (entityFilter && entityFilter !== "__all") params.set("entityType", entityFilter);
        if (personaFilter && personaFilter !== "__all") params.set("persona", personaFilter);
        if (actorFilter.trim()) params.set("actor", actorFilter.trim());
        if (searchFilter.trim()) params.set("search", searchFilter.trim());
        if (dateFrom) params.set("dateFrom", dateFrom);
        if (dateTo) params.set("dateTo", dateTo + "T23:59:59.999Z");
        const res = await fetch(
          `/api/audit-log${params.toString() ? `?${params.toString()}` : ""}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (cancelled) return;
        if (!res.ok) {
          const body = await res.json().catch(() => ({})) as { error?: string };
          setError(body.error ?? `Server error ${res.status}`);
          setEntries([]);
        } else {
          setEntries(await res.json() as AuditEntry[]);
        }
      } catch {
        if (!cancelled) setError("Network error. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => { cancelled = true; };
  }, [isAdmin, actionFilter, entityFilter, personaFilter, actorFilter, searchFilter, dateFrom, dateTo, getToken]);

  const hasFilters =
    (actionFilter && actionFilter !== "__all") ||
    (entityFilter && entityFilter !== "__all") ||
    (personaFilter && personaFilter !== "__all") ||
    actorFilter ||
    searchFilter ||
    dateFrom ||
    dateTo;

  const stats = useMemo(() => {
    const total = entries.length;
    const downloads = entries.filter((e) => e.action === "download").length;
    const uniqueActors = new Set(entries.map((e) => e.actorClerkUserId ?? e.actorName ?? "system")).size;
    return { total, downloads, uniqueActors };
  }, [entries]);

  if (orgLoading) {
    return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;
  }
  if (!isAdmin) {
    return (
      <div className="max-w-2xl mx-auto py-12 text-center space-y-3">
        <ShieldAlert className="h-10 w-10 mx-auto text-muted-foreground" />
        <h2 className="text-lg font-semibold">Admin access required</h2>
        <p className="text-sm text-muted-foreground">
          The organization audit log is only available to admins.
        </p>
        <Button asChild variant="outline" size="sm">
          <Link href="/settings">Back to Settings</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6 animate-fade-in" data-testid="audit-log-page">
      <div className="page-header">
        <h1 className="flex items-center gap-2">
          <ShieldAlert className="h-6 w-6 text-muted-foreground" />
          Audit Log
        </h1>
        <p>Organization-wide log of sensitive data access and changes. Visible to admins only.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground uppercase tracking-wider">Entries shown</div>
            <div className="text-2xl font-bold mt-1" data-testid="stat-total">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
              <Download className="h-3 w-3" /> Downloads
            </div>
            <div className="text-2xl font-bold mt-1" data-testid="stat-downloads">{stats.downloads}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground uppercase tracking-wider">Unique actors</div>
            <div className="text-2xl font-bold mt-1" data-testid="stat-actors">{stats.uniqueActors}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" /> Filters
          </CardTitle>
          <CardDescription>Search and narrow the log to investigate specific actions.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">Action</label>
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger data-testid="filter-action"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ACTION_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Entity</label>
              <Select value={entityFilter} onValueChange={setEntityFilter}>
                <SelectTrigger data-testid="filter-entity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ENTITY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Persona</label>
              <Select value={personaFilter} onValueChange={setPersonaFilter}>
                <SelectTrigger data-testid="filter-persona"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PERSONA_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Actor name</label>
              <Input
                data-testid="filter-actor"
                placeholder="e.g. Alex Chen"
                value={actorFilter}
                onChange={(e) => setActorFilter(e.target.value)}
              />
            </div>
            <div className="md:col-span-2 lg:col-span-1">
              <label className="text-xs text-muted-foreground flex items-center gap-1"><Search className="h-3 w-3" /> Search</label>
              <Input
                data-testid="filter-search"
                placeholder="filename, field, value…"
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">From</label>
              <Input data-testid="filter-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">To</label>
              <Input data-testid="filter-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
          </div>
          {hasFilters && (
            <div className="mt-3">
              <Button
                variant="ghost"
                size="sm"
                data-testid="filter-clear"
                onClick={() => {
                  setActionFilter("__all");
                  setEntityFilter("__all");
                  setPersonaFilter("__all");
                  setActorFilter("");
                  setSearchFilter("");
                  setDateFrom("");
                  setDateTo("");
                }}
              >
                <X className="h-4 w-4 mr-1" /> Clear filters
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Activity</CardTitle>
          <CardDescription>
            Showing up to 500 most recent matching entries, newest first.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 text-center text-sm text-muted-foreground flex flex-col items-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading audit entries…
            </div>
          ) : error ? (
            <div className="py-12 text-center text-sm text-destructive" data-testid="audit-error">
              {error}
            </div>
          ) : entries.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground" data-testid="audit-empty">
              <FileText className="h-8 w-8 mx-auto mb-2 opacity-40" />
              {hasFilters ? "No entries match these filters." : "No audit entries yet."}
            </div>
          ) : (
            <div className="space-y-2" data-testid="audit-list">
              {entries.map((e) => {
                const dl = downloadDetails(e);
                return (
                  <div
                    key={e.id}
                    className="border rounded-lg p-3 bg-muted/20 hover:bg-muted/40 transition-colors"
                    data-testid="audit-row"
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        {actionBadge(e.action)}
                        <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                          {ENTITY_LABELS[e.entityType]}
                        </Badge>
                        <span className="text-sm font-medium" data-testid="audit-actor">
                          {e.actorName ?? "System"}
                        </span>
                        {e.actorPersonas && e.actorPersonas.length > 0 && e.actorPersonas.map((p) => (
                          <PersonaBadge key={p} persona={p as Persona} size="xs" />
                        ))}
                        {e.actorIsGuest ? (
                          <Badge
                            variant="outline"
                            className="text-[10px] uppercase tracking-wider border-amber-500 text-amber-700"
                            data-testid="audit-guest-badge"
                          >
                            Guest
                          </Badge>
                        ) : null}
                      </div>
                      <span
                        className="text-xs text-muted-foreground"
                        title={new Date(e.createdAt).toISOString()}
                      >
                        {format(new Date(e.createdAt), "MMM d, yyyy h:mm a")}
                      </span>
                    </div>

                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      {e.entityHref ? (
                        <Link
                          href={e.entityHref}
                          className="text-xs font-medium text-primary hover:underline inline-flex items-center gap-1"
                          data-testid="audit-entity-link"
                        >
                          {e.entityLabel}
                          <ExternalLink className="h-3 w-3" />
                        </Link>
                      ) : (
                        <span className="text-xs font-medium text-muted-foreground">{e.entityLabel}</span>
                      )}
                    </div>

                    <div className="mt-2">
                      {summarize(e)}
                    </div>

                    {dl && (dl.ip || dl.userAgent) && (
                      <>
                        <Separator className="my-2" />
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-muted-foreground" data-testid="audit-download-meta">
                          {dl.ip && (
                            <div className="flex items-center gap-1.5">
                              <Globe className="h-3 w-3" />
                              <span className="font-medium text-foreground/80">IP:</span>
                              <span className="font-mono">{dl.ip}</span>
                            </div>
                          )}
                          {dl.userAgent && (
                            <div className="flex items-start gap-1.5 sm:col-span-2">
                              <Monitor className="h-3 w-3 mt-0.5 flex-shrink-0" />
                              <span className="font-medium text-foreground/80">User agent:</span>
                              <span className="font-mono break-all">{dl.userAgent}</span>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
